import React, { FC, useEffect } from 'react';
import { Field, Form } from 'react-final-form';
import { Link } from 'react-router-dom';

import cn from 'classnames';
import ThirdPartyAuthButtons from 'components/ThirdPartyAuthButtons';
import { FORM_ERROR } from 'final-form';
import { t } from 'ttag';

import { saveRegistrationSources } from '../../../../lib/helpers';

import HorizontalLineSeparator from '../../../ui/HorizontalLineSeparator';
import Button from '../../../ui/button/button';
import Checkbox from '../../../ui/checkbox/checkbox';
import Input from '../../../ui/input/input';

import { useAuthBrandingButtonClassName } from '../../../../hooks/authBranding/useAuthBrandingButtonClassName';
import { useAuthInputBorderClassName } from '../../../../hooks/authBranding/useAuthInputBorderClassName';
import {
    PRIVACY_POLICY_LINK,
    PUBLIC_OFFER_LINK_EDI,
    PUBLIC_OFFER_LINK_EDO,
    PUBLIC_OFFER_LINK_KASSA,
    PUBLIC_OFFER_LINK_KEP,
} from '../../../../lib/constants';
import { getLocationQuery, getReferrerQueries } from '../../../../lib/url';
import eventTracking from '../../../../services/analytics/eventTracking';
import auth from '../../../../services/auth';
import loggerService from '../../../../services/logger';
import PhoneInput from '../../../PhoneInput/PhoneInput';
import { useRegistrationRedirectLink } from '../../hooks/useRegistrationRedirectLink';
import { useRegistrationSource } from '../../hooks/useRegistrationSource';
import { useRegistrationSuccessEffect } from '../../hooks/useRegistrationSuccessEffect';
import { useVerifiedInviteTokenData } from '../../hooks/useVerifiedInviteTokenData';
import Panel from '../panel/panel';
import StepLabel from '../stepLabel/stepLabel';
import Title from '../title/title';
import AdditionalTrialText from './AdditionalTrialText/AdditionalTrialText';
import { getPasswordRuleByEmail, validate } from './validate';

import css from './userRegistrationForm.css';

const agreementText = (
    <span className={css.agreementText}>
        {t`Я погоджуюсь з офертою`}&nbsp;
        <a
            href={PUBLIC_OFFER_LINK_EDO}
            rel="noopener noreferrer"
            target="_blank"
        >
            {t`Вчасно`}
        </a>
        ,{' '}
        <a
            href={PUBLIC_OFFER_LINK_EDI}
            rel="noopener noreferrer"
            target="_blank"
        >
            {t`Вчасно.EDI`}
        </a>
        ,{' '}
        <a
            href={PUBLIC_OFFER_LINK_KASSA}
            rel="noopener noreferrer"
            target="_blank"
        >
            {t`Вчасно.Каса`}
        </a>
        ,{' '}
        <a
            href={PUBLIC_OFFER_LINK_KEP}
            rel="noopener noreferrer"
            target="_blank"
        >
            {t`Вчасно.КЕП`}
        </a>{' '}
        {t`та`}&nbsp;
        <a href={PRIVACY_POLICY_LINK} rel="noopener noreferrer" target="_blank">
            {t`політикою конфіденційності`}
        </a>
    </span>
);

const trackEvent = (action: string, label?: string) => {
    eventTracking.sendEvent('form-signup-new-step1', action, label);
};

/**
 *
 * @deprecated
 * @see `cs/components/registration/components/userRegistrationForm/new/NewUserRegistrationForm.tsx`
 */
const UserRegistrationForm: FC<React.PropsWithChildren<unknown>> = () => {
    const { token: jwtToken, ref, activeProTrial, ...other } = getLocationQuery(
        location,
    );
    const authBrandingButtonClassName = useAuthBrandingButtonClassName();
    const authInputBorderClassName = useAuthInputBorderClassName();
    const referrerQueries = getReferrerQueries();
    const sourceLink = useRegistrationSource();
    const redirectLink = useRegistrationRedirectLink();
    const registrationSuccessEffect = useRegistrationSuccessEffect(sourceLink);
    const { edrpou, email, token } = useVerifiedInviteTokenData();
    const isTTNAppContext = sourceLink === 'ttn';

    const isActiveProTrial = Boolean(activeProTrial);

    const handleSubmit = async (values: Record<string, string>) => {
        try {
            await auth.register({
                name: values.username,
                email: values.email,
                phone: values.phone,
                password: values.password,
                token,
                referrer: ref,
                redirect: redirectLink,
                source: sourceLink,
                isActiveProTrial,
            });

            eventTracking.sendToGTM({
                event: 'main_reg_event',
                category: 'vchasno_main_reg',
                action: values.email,
            });
            await registrationSuccessEffect();
        } catch (err) {
            trackEvent('error', err.message);
            loggerService.error('Error on register user', {
                err: err.message,
                email: values.email,
            });
            const passwordError = err.details?.password;
            const userNameError = err.details?.name?.[0] || err.details?.name; // depends on the backend libraries response
            return {
                password: passwordError,
                username: userNameError,
                [FORM_ERROR]: err.message,
            };
        }

        return {};
    };

    useEffect(() => {
        trackEvent('show');
        saveRegistrationSources({
            ...referrerQueries,
            ...other,
            page: 'registration',
            referrer: document.referrer,
            ref,
            registration_source: jwtToken ? 'after_invite' : 'main',
        });
    }, []);

    if (jwtToken && !edrpou) {
        return (
            <Panel>
                <div className={css.root}>
                    <Title className={css.errorTitle}>{t`Помилка`}</Title>
                    <div>
                        {t`Виникла помилка при спробі реєстрації. Неможливо
                        розібрати унікальний токен, необхідний для реєстрації.
                        Спробуйте ще раз перейти за посиланням на реєстрацію з
                        листа, відправленого на вашу електронну пошту.`}
                    </div>
                    <div>
                        {t`Будь ласка, перейдіть на`}{' '}
                        <a href="/">{t`головну сторінку`}</a>.
                    </div>
                </div>
            </Panel>
        );
    }

    const additionalTrialTextComponent = isActiveProTrial ? (
        <AdditionalTrialText />
    ) : null;

    const buttonText = isActiveProTrial
        ? t`Почати безкоштовно`
        : t`Зареєструватись`;

    return (
        <Panel
            className={css.container}
            mainPanelClassName={cn(
                {
                    [css.containerBorderRadius]: !additionalTrialTextComponent,
                },
                css.formContainer,
            )}
            leftPanel={additionalTrialTextComponent}
        >
            <StepLabel className={css.stepLabel}>{t`Крок 1/4`}</StepLabel>
            <Title>{t`Зареєструватись`}</Title>
            <div className={css.hint}>
                {t`Ви вже маєте обліковий запис?`}{' '}
                <Link
                    className={css.link}
                    to={(location) => ({
                        ...location,
                        pathname: '/auth/login',
                    })}
                >
                    {t`Увійти`}
                </Link>
            </div>
            {config.GOOGLE_AUTH_ON && !isTTNAppContext && (
                <>
                    <ThirdPartyAuthButtons
                        microsoftAuthOn
                        googleAuthOn
                        appleAuthOn
                    />
                    <HorizontalLineSeparator
                        className={css.horizontalLineSeparator}
                    >{t`або`}</HorizontalLineSeparator>
                </>
            )}
            <Form
                onSubmit={handleSubmit}
                validate={validate}
                initialValues={{ email, agreement: true }}
            >
                {(formProps) => {
                    const passwordMinLength = getPasswordRuleByEmail(
                        formProps.values.email,
                    ).minLength;

                    return (
                        <form
                            onSubmit={formProps.handleSubmit}
                            className={css.form}
                            data-qa="qa_registration_form"
                        >
                            <Field name="email">
                                {({ input, meta }) => (
                                    <div className={css.inputWrapper}>
                                        <label htmlFor={input.name}>
                                            {t`Email`}
                                        </label>
                                        <Input
                                            id={input.name}
                                            type="text"
                                            placeholder={t`Вкажіть свій email`}
                                            value={input.value}
                                            onChange={input.onChange}
                                            error={
                                                meta.error && meta.submitFailed
                                            }
                                            errorMessage={meta.error}
                                            dataQa="qa_email_input"
                                            className={authInputBorderClassName}
                                        />
                                    </div>
                                )}
                            </Field>
                            <Field name="password">
                                {({ input, meta }) => (
                                    <div className={css.inputWrapper}>
                                        <label htmlFor={input.name}>
                                            {t`Пароль`}
                                        </label>
                                        <Input
                                            id={input.name}
                                            type="password"
                                            placeholder={t`Вигадайте пароль`}
                                            hint={t`Має містити щонайменше ${passwordMinLength} символів, хоча б одну цифру і один символ (!'#$%*-/:;=?)`}
                                            value={input.value}
                                            dataQa="qa_password_input"
                                            onChange={input.onChange}
                                            error={
                                                (meta.error ||
                                                    meta.submitError) &&
                                                meta.dirty
                                            }
                                            valid={
                                                !meta.error &&
                                                !meta.submitError &&
                                                meta.dirty
                                            }
                                            errorMessage={
                                                meta.error || meta.submitError
                                            }
                                            validMessage={t`Чудово!`}
                                            className={authInputBorderClassName}
                                        />
                                    </div>
                                )}
                            </Field>
                            <Field name="phone">
                                {({ input, meta }) => (
                                    <div className={css.inputWrapper}>
                                        <PhoneInput
                                            wide
                                            label={t`Номер телефону`}
                                            value={input.value}
                                            onChange={(value) =>
                                                input.onChange(`+${value}`)
                                            }
                                            inputProps={{
                                                id: input.name,
                                                'data-qa': 'qa_phone_input',
                                            }}
                                            className={authInputBorderClassName}
                                            error={
                                                meta.submitFailed && meta.error
                                            }
                                        />
                                    </div>
                                )}
                            </Field>
                            <Field name="username">
                                {({ input, meta }) => (
                                    <div className={css.inputWrapper}>
                                        <label htmlFor={input.name}>
                                            {t`ПІБ`}
                                        </label>
                                        <Input
                                            id={input.name}
                                            type="text"
                                            placeholder={t`Як ми можемо звернутись до вас?`}
                                            value={input.value}
                                            dataQa="qa_username_input"
                                            onChange={input.onChange}
                                            error={
                                                (meta.error ||
                                                    meta.submitError) &&
                                                meta.dirty
                                            }
                                            errorMessage={
                                                'Довжина ПІБ не повинна перевищувати 64 символи'
                                            }
                                            className={authInputBorderClassName}
                                        />
                                    </div>
                                )}
                            </Field>
                            {formProps.submitError &&
                                formProps.submitFailed &&
                                !formProps.dirtySinceLastSubmit && (
                                    <div className={css.error}>
                                        {formProps.submitError}
                                    </div>
                                )}
                            <Field name="agreement">
                                {({ input }) => (
                                    <div className={css.checkboxWrapper}>
                                        <Checkbox
                                            name="agreement"
                                            text={agreementText}
                                            checked={input.value}
                                            dataQa="qa_agreement_checkbox"
                                            onChange={input.onChange}
                                        />
                                    </div>
                                )}
                            </Field>
                            <div className={css.button}>
                                <Button
                                    type="submit"
                                    width="full"
                                    dataQa="qa_submit_form"
                                    disabled={!formProps.values.agreement}
                                    isLoading={formProps.submitting}
                                    className={authBrandingButtonClassName}
                                >
                                    {buttonText}
                                </Button>
                            </div>
                        </form>
                    );
                }}
            </Form>
        </Panel>
    );
};

export default UserRegistrationForm;
